import {FC, useEffect, useState} from 'react'
import {FAQDataType} from '../FAQ'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {useAuth} from 'app/modules/auth'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {collection, addDoc, updateDoc, doc, serverTimestamp, Timestamp} from '@firebase/firestore'
import db from 'services/firebase'
import {showSuccessToast} from 'app/components/ShowToast'
import Input from 'app/components/Input'
import Textarea from 'app/components/Textarea'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  faq?: FAQDataType
}
const validationSchema = Yup.object().shape({
  question: Yup.string()
    .min(3, 'Minimum 10 characters')
    .max(200, 'Maximum 200 characters')
    .required('Question is required'),
  answer: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(800, 'Maximum 800 characters')
    .required('Answer is required'),
})

const AddEditFAQModal: FC<Props> = ({isModalOpen, hideModal, isEditMode, faq}) => {
  const {currentUser} = useAuth()

  const [loading, setLoading] = useState(false)

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<FAQDataType>({
    initialValues: {id: '', question: '', answer: '', addedBy: ''},
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)

        const tenantId = currentUser?.tenantId || ''
        const faqsRef = collection(db, 'tenants', tenantId, 'trust_center', 'faqs', 'all_faqs')

        if (!isEditMode) {
          const newDocRef = await addDoc(faqsRef, {
            question: values.question,
            answer: values.answer,
            addedBy: currentUser?.uid,
            updatedAt: Timestamp.fromDate(new Date()),
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${values.question} FAQ for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'FAQ added',
          })

          showSuccessToast({message: 'FAQ has been added successfully'})
        } else {
          await updateDoc(doc(faqsRef, faq?.id), {
            question: values.question,
            answer: values.answer,
            addedBy: currentUser?.uid,
            updatedAt: Timestamp.fromDate(new Date()),
          })
          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${faq?.question} FAQ for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'FAQ updated',
          })
          showSuccessToast({message: 'FAQ has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating faq for trust center')
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (faq?.id) {
      formik.setValues({
        id: faq.id,
        question: faq.question,
        answer: faq.answer,
        addedBy: faq.addedBy,
      })
    }
  }, [faq])

  const okBtnClickHandler = async () => {
    formik.submitForm()
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit FAQ' : 'Add FAQ'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add FAQ'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding FAQ...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Input
          type='text'
          label='Question'
          placeholder='FAQ Question'
          error={formik.touched.question && formik.errors.question ? formik.errors.question : ''}
          {...formik.getFieldProps('question')}
        />

        <Textarea
          label='Answer'
          placeholder='Type FAQ Answer...'
          error={formik.touched.answer && formik.errors.answer ? formik.errors.answer : ''}
          {...formik.getFieldProps('answer')}
        />
      </div>
    </CustomModal>
  )
}

export default AddEditFAQModal
